import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './Marketplace.scss';
import { Container } from 'react-bootstrap';
import { COURSES } from './data/Mockdata';
import { fetchCommunities, setSelectedCategory } from '../../store/slices/communitySlice';
import { fetchCourses, fetchRecommended, fetchTrending } from '../../store/slices/marketplaceSlice';
import { fetchTeachers } from '../../store/slices/teachersSlice';
import { getCategoriesApi } from '../../api/communities';
import { CourseCard } from './components/CourseCard';
import { InstructorCard } from './components/InstructorCard';

const Marketplace = () => {
  const dispatch = useDispatch();

  // ── Community state ──
  const { filters } = useSelector((state) => state.community);

  // ── Marketplace state from Redux ──
  const { courses, recommended, trending, loading: marketplaceLoading } = useSelector((state) => state.marketplace);

  // ── Teachers state from Redux ──
  const { teachers, loading: teachersLoading } = useSelector((state) => state.teachers);

  // ── Local UI state ──
  const [localCategories, setLocalCategories] = useState([{ _id: '', name: 'All Communities', icon: '🌐' }]);
  const [activeKeyword, setActiveKeyword] = useState('Language');
  const [courseIndex] = useState(0);
  const [instructorIndex] = useState(0);

  const VISIBLE_COUNT = 5;

  // ── Fetch all data on mount ──
  useEffect(() => {
    dispatch(fetchCourses());
    dispatch(fetchRecommended());
    dispatch(fetchTrending());
    dispatch(fetchTeachers());
  }, [dispatch]);

  // ── Fetch categories ──
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategoriesApi();
        if (data?.data) {
          const apiCategories = data.data.map((cat) => {
            const match = cat.name.match(/(.+?)([\u{1F300}-\u{1FAFF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}])?$/u);
            return {
              _id: cat._id,
              name: match ? match[1].trim() : cat.name,
              icon: match && match[2] ? match[2] : '',
            };
          });
          setLocalCategories([{ _id: '', name: 'All Communities', icon: '🌐' }, ...apiCategories]);
        }
      } catch {
        setLocalCategories([{ _id: '', name: 'All Communities', icon: '🌐' }]);
      }
    };
    loadCategories();
  }, []);

  // ── Fetch communities when filter changes ──
  useEffect(() => {
    dispatch(fetchCommunities({ category: filters.selectedCategory }));
  }, [dispatch, filters.selectedCategory]);

  const handleCategoryClick = (categoryId) => {
    dispatch(setSelectedCategory(categoryId));
  };

  // ── Map raw API course data to card-compatible shape ──
  const mapCourseData = (c) => {
    const priceVal = c.price !== undefined ? c.price : c.cost !== undefined ? c.cost : c.class_fees;
    const studentsCount = Array.isArray(c.students)
      ? c.students.length
      : c.students !== undefined
      ? c.students
      : c.members_count || Math.floor(Math.random() * 100) + 10;

    return {
      id: c._id || c.id,
      category: c.category?.name || c.category || 'General',
      categoryColor: c.categoryColor || 'primary',
      image: c.cover_image,
      title: c.title || c.name || 'Untitled Course',
      price: priceVal > 0 ? `$${priceVal}` : 'Free',
      rating: parseFloat(c.teacher_reviews?.average_rating || c.rating || c.avg_rating || 5.0),
      students: studentsCount,
    };
  };

  return (
    <div className="marketplace-page">

      {/* ── Trending Courses ── */}
      <section className="best-selling-courses-section section-padding">
        <Container fluid="xl">
          <div className="section-heading-wrapper">
            <div className="section-heading">Trending <span>Courses</span></div>
            <div className="description">Find the best trending courses, mentors, and sessions for your needs.</div>
          </div>

          {marketplaceLoading.trending ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
          ) : (
            <div className="g-3 g-xxl-4 row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5">
              {(trending.length > 0 ? trending : COURSES).slice(courseIndex, courseIndex + VISIBLE_COUNT).map((c) => (
                <div className="item-card" key={c._id || c.id}>
                  <CourseCard course={trending.length > 0 ? mapCourseData(c) : c} />
                </div>
              ))}
            </div>
          )}

          {/* Popular Keywords */}
          <div className="d-flex flex-column gap-3 mt-5">
            <span className="fs-6 fw-semibold text-dark me-1">Popular keywords:</span>
            <span className="d-flex flex-wrap align-items-center gap-2">
              {[
                'Language', 'Education', 'ESL', 'Communication', 'Literacy', 'Vocabulary', 'Pronunciation', 'Speaking',
                'Science', 'Mathematics', 'Reading', 'Writing', 'Grammar', 'Phonetics', 'Linguistics',
                'Business', 'Healthcare', 'Training', 'Career', 'Workplace',
                'Community', 'Culture', 'Conversation', 'Coaching', 'Tutoring',
              ].map((kw) => (
                <span
                  key={kw}
                  className={`small pointer px-3 py-2 rounded-pill ${activeKeyword === kw ? 'bg-primary text-white' : 'bg-secondary text-dark bg-opacity-10'}`}
                  onClick={() => setActiveKeyword(kw)}
                >
                  {kw}
                </span>
              ))}
            </span>
          </div>
        </Container>
      </section>

      {/* ── Recommended Courses ── */}
      <section className="best-selling-courses-section section-padding bg-secondary bg-opacity-10">
        <Container fluid="xl">
          <div className="section-heading-wrapper">
            <div className="section-heading">Recommended <span>Courses</span></div>
            <div className="description">Courses recommended just for you based on your interests.</div>
          </div>

          {marketplaceLoading.recommended ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
          ) : (
            <div className="g-3 g-xxl-4 row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5">
              {recommended.length > 0
                ? recommended.slice(0, VISIBLE_COUNT).map((c) => (
                    <div className="item-card" key={c._id || c.id}>
                      <CourseCard course={mapCourseData(c)} />
                    </div>
                  ))
                : <div className="text-muted">No recommended courses found.</div>}
            </div>
          )}
        </Container>
      </section>

      {/* ── All Courses ── */}
      <section className="best-selling-courses-section section-padding">
        <Container fluid="xl">
          <div className="section-heading-wrapper">
            <div className="section-heading">All <span>Courses</span></div>
            <div className="description">Browse our entire library of courses.</div>
          </div>

          {marketplaceLoading.courses ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
          ) : (
            <div className="g-3 g-xxl-4 row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5">
              {courses.length > 0
                ? courses.slice(0, VISIBLE_COUNT).map((c) => (
                    <div className="item-card" key={c._id || c.id}>
                      <CourseCard course={mapCourseData(c)} />
                    </div>
                  ))
                : <div className="text-muted">No courses found.</div>}
            </div>
          )}
        </Container>
      </section>

      {/* ── Popular Instructors ── */}
      <section className="popular-instructor-section bg-secondary bg-opacity-10 section-padding">
        <Container fluid="xl">
          <div className="section-heading-wrapper">
            <div className="section-heading">Popular <span>Mentors</span></div>
            <div className="description">Learn from our top-rated industry experts.</div>
          </div>
          
          {teachersLoading ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
          ) : (
            <div className="g-3 g-xxl-4 row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5">
              {teachers.slice(instructorIndex, instructorIndex + VISIBLE_COUNT).map((inst) => (
                <div className="item-card" key={inst._id}>
                  <InstructorCard instructor={inst} />
                </div>
              ))}
            </div>
          )}
        </Container>
      </section>

      {/* ── Top Mentors ── */}
      <section className="top-mentor-section section-padding">
        <Container fluid="xl">
          <div className="section-heading-wrapper">
            <div className="section-heading">Top <span>Mentors</span></div>
            <div className="description">Connect with world-class instructors from across the globe.</div>
          </div>
          
          {teachersLoading ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
          ) : (
            <div className="g-3 g-xxl-4 row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5">
              {teachers.length > 0 ? (
                teachers.map((inst) => (
                  <div className="item-card mb-4" key={inst._id}>
                    <InstructorCard instructor={inst} />
                  </div>
                ))
              ) : (
                <div className="text-muted">No mentors found at this time.</div>
              )}
            </div>
          )}
        </Container>
      </section>

    </div>
  );
};

export default Marketplace;
